import { Anchor, Grid, Group, Text } from '@mantine/core';
import { PatientTrackLabel, Track } from '@bariendo/lib/constants';
import { ORBIT_DISABLED, ORBIT_GRAY } from '@/theme/colors';
import { LockedValue } from './LockedValue';

interface TrackRowProps {
  /** Undefined when no active EpisodeOfCare carries a track. */
  track: Track | undefined;
  /** Absent for a viewer outside PAYMENTS_ROLES, who reads the track with no Change link. */
  onChange?: () => void;
  /** Set once the track is fixed (from Won): a lock and this reason instead of the Change link. */
  lockedReason?: string;
}

/**
 * The patient's go-to-market track, with a way to change it.
 *
 * **Read-only plus an explicit action, not a select.** The two editable rows
 * beside it — Package and Location — are cheap, reversible corrections to what a
 * rep heard on a call. A track change scrubs a CRM record, closes a care
 * episode and decides whether this patient is ever asked to pay. Dressing it in
 * the same chrome understates it, and a select commits the operator to a
 * destination before the dialog has told them what it does.
 *
 * Renders even when the track is unresolved, unlike `PanelRow`. An absent
 * read-only row means "this didn't happen"; here it would mean "you can't
 * change this", which is wrong — and "no track is recorded" is itself the fact
 * an operator most needs to see, since most self-pay patients have no
 * `EpisodeOfCare` at all.
 */
export function TrackRow({ track, onChange, lockedReason }: TrackRowProps) {
  const label = (
    <Text style={{ fontSize: 14, lineHeight: '20px', color: track ? undefined : ORBIT_DISABLED }}>
      {track ? PatientTrackLabel[track] : 'Not recorded'}
    </Text>
  );

  return (
    <Grid align="flex-start" style={{ width: '100%' }}>
      <Grid.Col span={3}>
        <Text style={{ fontSize: 13, lineHeight: '16px', color: ORBIT_GRAY }}>Track</Text>
      </Grid.Col>
      <Grid.Col span={9}>
        {lockedReason ? (
          <LockedValue reason={lockedReason}>{label}</LockedValue>
        ) : (
          <Group gap={8} wrap="nowrap">
            {label}
            {onChange && (
              <Anchor component="button" type="button" onClick={onChange} style={{ fontSize: 13, lineHeight: '16px' }}>
                Change
              </Anchor>
            )}
          </Group>
        )}
      </Grid.Col>
    </Grid>
  );
}
